import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import i18n from '../i18n';

/**
 * Axios instance for user-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for user-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
// Create axios instance for user API calls
const userAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

/**
 * Request interceptor
 * - Attaches stored auth token when present. Includes a short debug log
 *   which can help during development. Remove or gate this log in prod.
 */
// Add request interceptor to include auth token
userAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Debug log to verify token is being sent (remove in production)
      console.log('Sending Bearer token with userAPI request:', token.substring(0, 20) + '...');
    } else {
      console.warn('No auth token found in storage for userAPI request');
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Clears local/session storage and redirects to login on 401 responses
 */
// Add response interceptor to handle auth errors
userAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Normalize axios error objects into human-readable messages.
 * - Tries common server response shapes and falls back to generic text
 */
// Helper to extract backend error details from axios error

/**
 * User Service Class
 * Handles all API operations related to users
 */
/**
 * UserService
 * - Wrapper class for user-related API operations
 * - Provides get/create/update/delete/toggle/export helpers
 */
class UserService {
  /**
   * Get all users with optional pagination and filtering
   * @param {Object} params - Query parameters (page, limit, search, filter)
   * @returns {Promise<Object>} Users list with pagination info
   */
  async getUsers(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters - API expects 'skip' instead of 'page'
      if (params.page !== undefined) {
        const skip = (params.page - 1) * (params.limit || 20);
        queryParams.append('skip', skip);
      } else if (params.skip !== undefined) {
        queryParams.append('skip', params.skip);
      } else {
        queryParams.append('skip', 0); // Default skip value
      }

      if (params.limit) queryParams.append('limit', params.limit);
      else queryParams.append('limit', 20); // Default limit value

      if (params.search !== undefined && params.search !== '') {
        queryParams.append('search', params.search);
      }

      if (params.is_active !== undefined && params.is_active !== null && params.is_active !== '' && params.is_active !== 'all') {
        queryParams.append('is_active', params.is_active);
      }

      const response = await userAPI.get(`/users?${queryParams.toString()}`);

      const data = response.data;
      return {
        success: true,
        data: data?.users || data?.items || data || [],
        pagination: data?.pagination || {},
        total: data?.total || data?.count || (Array.isArray(data) ? data.length : 0),
      };
    } catch (error) {
      console.error('Get users error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get user by ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} User details
   */
  async getUserById(userId) {
    try {
      const response = await userAPI.get(`/users/${userId}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get user by ID error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Create new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user
   */
  async createUser(userData) {
    try {
      const response = await userAPI.post('/users', userData);

      return {
        success: true,
        data: response.data,
        message: 'User created successfully',
      };
    } catch (error) {
      console.error('Create user error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Update user
   * @param {number} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(userId, userData) {
    try {
      // Check if self-edit
      const userDataStr = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
      let currentUserId = null;
      if (userDataStr) {
        try {
          const u = JSON.parse(userDataStr);
          currentUserId = u?.id || u?.user_id;
        } catch (e) {}
      }
      if (!currentUserId) {
        const token = getAuthToken();
        if (token) {
          try {
            const payload = token.split('.')[1];
            const decoded = JSON.parse(atob(payload));
            currentUserId = decoded.user_id;
          } catch (e) {}
        }
      }

      let response;
      if (currentUserId && String(userId) === String(currentUserId)) {
        // self-edit: strip user_type / is_active
        const { user_type, is_active, ...payloadWithoutRoleOrIsActive } = userData;
        response = await userAPI.put('/users/me', payloadWithoutRoleOrIsActive);
      } else {
        response = await userAPI.put(`/users/${userId}`, userData);
      }

      return {
        success: true,
        data: response.data,
        message: 'User updated successfully',
      };
    } catch (error) {
      console.error('Update user error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Delete user
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteUser(userId) {
    try {
      const token = getAuthToken();
      console.log('Using Bearer token for deleteUser:', token);
      await userAPI.delete(`/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: 'User deleted successfully',
      };
    } catch (error) {
      console.error('Delete user error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Bulk delete users
   * @param {Array} userIds - Array of user IDs
   * @returns {Promise<Object>} Bulk deletion result
   */
  async bulkDeleteUsers(userIds) {
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      // Normalize input so callers can pass either an array or a single id
      const idsArray = Array.isArray(userIds) ? userIds : (userIds ? [userIds] : []);
      if (idsArray.length === 0) {
        return { success: false, error: i18n.t('hookErrors.noSelection.userIds') };
      }

      const payload = { user_ids: idsArray };
      console.log('Bulk delete payload:', payload);
      console.log('Using Bearer token for bulkDeleteUsers:', token ? `${token.substring(0, 20)}...` : token);

      // Use POST for bulk-delete to avoid accidental routing to /users/{user_id} which expects an integer
      const response = await userAPI.post('/users/bulk-delete', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        data: response.data,
        message: `${idsArray.length} users deleted successfully`,
      };
    } catch (error) {
      console.error('Bulk delete users error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Toggle user active status
   * @param {number} userId - User ID
   * @param {boolean} isActive - New active status
   * @returns {Promise<Object>} Update result
   */
  async toggleUserStatus(userId, isActive) {
    try {
      const response = await userAPI.patch(`/users/${userId}/status`, {
        is_active: isActive
      });

      return {
        success: true,
        data: response.data,
        message: `User ${isActive ? 'activated' : 'deactivated'} successfully`,
      };
    } catch (error) {
      console.error('Toggle user status error:', error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Export users data
   * @param {Object} filters - Export filters
   * @returns {Promise<Object>} Export data
   */
  async exportUsers(filters = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Set format, defaulting to csv
      const format = filters.format || 'csv';
      queryParams.append('format', format);

      // Add filters if provided
      if (filters.user_type) queryParams.append('user_type', filters.user_type);
      if (filters.is_active !== undefined && filters.is_active !== null && filters.is_active !== '' && filters.is_active !== 'all') {
        queryParams.append('is_active', filters.is_active);
      }
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);

      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      console.log('Using Bearer token for exportUsers:', token);

      const response = await userAPI.get(`/users/export/download?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Export users error:', error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Get delivery supervisors with their current status
   * @returns {Promise<Object>} List of supervisors with status
   */
  async getDeliverySupervisorsStatus() {
    try {
      const response = await userAPI.get('/users/delivery-supervisors/status');
      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      console.error('Get delivery supervisors status error:', error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get labours list
   * @returns {Promise<Object>} List of labours
   */
  async getLabours() {
    try {
      const response = await userAPI.get('/users/labours');
      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      console.error('Get labours error:', error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Reset user password
   * @param {number} userId - User ID
   * @param {Object} passwordData - New password and confirm password
   * @returns {Promise<Object>} Reset result
   */
  async resetUserPassword(userId, passwordData) {
    try {
      const response = await userAPI.put(`/users/${userId}/reset-password`, passwordData);
      return {
        success: true,
        data: response.data,
        message: 'Password reset successfully',
      };
    } catch (error) {
      console.error('Reset password error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;
